import Button from "@/components/Button";
import IconArrowRight from "@/components/icons/IconArrowRight";
import IconPlus from "@/components/icons/IconPlus";
import LegalDisclaimer from "@/components/LegalDisclaimer";
import { BADGE_ICONS } from "@/constants/components/tabsBento";
import useElementViewed from "@/hooks/tagular/use-element-viewed";
import cn from "@/lib/cn";
import getTextFromComponent from "@/lib/get-text-from-component";
import fireElementClicked from "@/lib/tagular/fire-element-clicked";
import { resolveXfinityClickOffUrl } from "@/lib/clickthrough-destination";
import Image from "next/image";
import React from "react";
import type { BentoCardButton, BentoCardProps } from "../../types";
import ZoomWrapper from "./ZoomWrapper";

const BentoCard: React.FC<BentoCardProps> = ({
  title,
  subtitle,
  price,
  priceClassName = "",
  description,
  customContent,
  bottomCta,
  buttons = [],
  buttonContainerClassName,
  backgroundImage,
  mobileBackgroundImage,
  backgroundPosition,
  theme = "light",
  badge,
  badgeClassName,
  logoImage,
  logoAlt = "Logo",
  legalText,
  className = "",
  gridColumn,
  gridRow,
  minHeight = "400px",
  tabletMinHeight = "560px",
  mobileMinHeight = "560px",
  padding = "2rem",
  mobilePadding,
  titleClassName = "",
  descriptionClassName = "",
  useCustomPadding = false,
  contentAlignment = "top",
  elementName,
  mobileContentAlignment,
  contentWidth = "standard",
  onButtonClick,
  tagularProps,
  enableContentZoom = false,
}) => {
  const cardRef = React.useRef<HTMLDivElement>(null);
  useElementViewed(
    cardRef,
    {
      elementType: "card",
      location: "bento",
      name: `cardBento${elementName}`,
      text: getTextFromComponent(title ?? ""),
      ...tagularProps,
    },
    {
      threshold: 0.25,
    }
  );

  const handleButtonClick = (action: string) => {
    if (onButtonClick) {
      onButtonClick(action);
    } else {
      console.log(`${action} button clicked`);
    }
  };

  const renderBadge = (badgeKey: keyof typeof BADGE_ICONS) => {
    const badgeValue = BADGE_ICONS[badgeKey];

    // Check if it's a URL string or a React component
    if (typeof badgeValue === "string") {
      return (
        <div className="mb-4">
          <Image
            src={badgeValue}
            alt="Badge"
            width={160}
            height={48}
            className={cn("h-12", badgeClassName)}
            loading="lazy"
          />
        </div>
      );
    }

    // It's a React component
    const BadgeComponent = badgeValue as React.ComponentType<{
      className?: string;
    }>;
    return (
      <div className="mb-4">
        <BadgeComponent className={badgeClassName} />
      </div>
    );
  };

  const renderLogoImage = () => {
    if (!logoImage) return null;
    return (
      <div className="mb-4">
        <Image
          src={logoImage}
          alt={logoAlt}
          width={120}
          height={40}
          loading="lazy"
          className="w-auto h-8"
        />
      </div>
    );
  };

  const renderButton = (button: BentoCardButton, index: number) => {
    const resolvedHref = resolveXfinityClickOffUrl(button.href);
    return (
      <Button
        key={index}
        href={resolvedHref}
        onClick={
          resolvedHref
            ? () => {
                fireElementClicked({
                  elementType: "ctaButton",
                  text: button.text,
                  position: index + 1,
                  location: "bento",
                  name: `ctaButtonBento${elementName}`,
                  outboundUrl: resolvedHref,
                  ...button.tagularProps,
                  ...tagularProps,
                });
              }
            : () => {
                handleButtonClick(button.action);
                fireElementClicked({
                  elementType: "textLink",
                  text: button.text,
                  position: index + 1,
                  location: "bento",
                  name: `textLinkBento${elementName}Modal`,
                  ...button.tagularProps,
                  ...tagularProps,
                });
              }
        }
        display={button.display}
        tone={button?.tone}
        size={button.size}
        className={button.className}
        title={button.text}
      >
        {button.contentType === "icon" ? (
          button.iconType === "plus" ? (
            <IconPlus theme="white" className="w-4" title={button.text} />
          ) : (
            <IconArrowRight
              theme={button.iconTheme ?? "white"}
              className="w-4"
              title={button.text}
            />
          )
        ) : (
          button.text
        )}
      </Button>
    );
  };

  // Create a unique id for this card for responsive styling
  const cardId = React.useId().replace(/:/g, "");

  const cardStyle: React.CSSProperties = {
    "--image-url": backgroundImage ? `url("${backgroundImage}")` : undefined,
    backgroundPosition,
    minHeight,
    maxHeight: minHeight,
    // Padding is applied through utilities reading these variables rather than
    // as an inline declaration, so the mobile value can win on specificity
    // alone instead of needing !important.
    "--card-padding": enableContentZoom ? undefined : padding,
    "--card-padding-mobile": enableContentZoom
      ? undefined
      : (mobilePadding ?? padding),
    gridColumn,
    gridRow,
  } as React.CSSProperties;

  const textColorClass = theme === "dark" ? "text-inverse-white" : "text-black";
  const hasResponsiveCardStyles =
    tabletMinHeight !== minHeight ||
    mobileMinHeight !== minHeight ||
    Boolean(mobileBackgroundImage);

  return (
    <>
      {hasResponsiveCardStyles ? (
        <style
          dangerouslySetInnerHTML={{
            __html: `
            @media (max-width: 1038px) {
              .bento-card-${cardId} {
                min-height: ${tabletMinHeight} !important;
                max-height: ${tabletMinHeight} !important;
              }
            }
            @media (max-width: 767px) {
              .bento-card-${cardId} {
                ${
                  mobileBackgroundImage
                    ? `--image-url: url("${mobileBackgroundImage}") !important;`
                    : ""
                }
                min-height: ${mobileMinHeight} !important;
                max-height: ${mobileMinHeight} !important;
                background-size: cover !important;
                background-position: bottom center !important;
                background-repeat: no-repeat !important;
              }
            }
          `,
          }}
        />
      ) : null}
      {enableContentZoom ? (
        <style
          dangerouslySetInnerHTML={{
            __html: `
            .bento-card-${cardId} {
              cursor: pointer;
            }

            .bento-card-inner-${cardId} {
              transition: transform 0.3s ease-in-out;
              height: 100%;
              width: 100%;
              flex: 1;
              background-image: var(--image-url);
              background-size: cover;
              background-position: center center;
              background-repeat: no-repeat;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              border-radius: 1rem;
              padding: ${padding};
            }

            .bento-card-${cardId}:hover .bento-card-inner-${cardId} {
              transform: scale(1.05);
            }
          `,
          }}
        />
      ) : null}
      <div
        className={cn(
          "relative overflow-hidden flex justify-start flex-col w-full h-full",
          {
            "rounded-2xl": !enableContentZoom,
            "p-(--card-padding) max-md:p-(--card-padding-mobile)":
              !enableContentZoom,
            "bg-[image:var(--image-url)] bg-cover bg-center md:bg-cover":
              !!backgroundImage && !enableContentZoom,
          },
          `bento-card-${cardId}`,
          className
        )}
        style={cardStyle}
        ref={cardRef}
      >
        <ZoomWrapper
          enableContentZoom={enableContentZoom}
          bottomCta={bottomCta}
          elementName={elementName}
          title={title}
          tagularProps={tagularProps}
          cardId={cardId}
        >
          <div
            className={cn(
              "relative z-10 w-full flex flex-col",
              { "max-w-125": contentWidth === "500" },
              { "max-w-100": contentWidth === "400" },
              { "max-w-[325px]": contentWidth === "325" },
              { "max-w-125 lg:max-w-100": contentWidth === "standard" },
              { "max-w-full": contentWidth === "full" },
              contentAlignment === "center" &&
                (mobileContentAlignment === "top"
                  ? "justify-start lg:justify-center h-full"
                  : mobileContentAlignment === "bottom"
                    ? "mt-auto lg:justify-center h-full"
                    : "justify-center h-full"),
              useCustomPadding ? "px-4 xl:px-8 py-8 md:pt-14 md:pb-4" : "",
              textColorClass
            )}
          >
            {logoImage ? renderLogoImage() : badge && renderBadge(badge)}

            {subtitle ? (
              <h3 className="my-4 text-headline2">{subtitle}</h3>
            ) : null}

            {titleClassName === "patch-text-headline3" ? (
              <h3 className={cn("mb-4", titleClassName)}>
                {typeof title === "string"
                  ? title.split("\n").map((line, index) => (
                      <span key={index}>
                        {line}
                        {index < title.split("\n").length - 1 && <br />}
                      </span>
                    ))
                  : title}
              </h3>
            ) : (
              <h2 className={cn("text-display2 mb-4", titleClassName)}>
                {typeof title === "string"
                  ? title.split("\n").map((line, index) => (
                      <span key={index}>
                        {line}
                        {index < title.split("\n").length - 1 && <br />}
                      </span>
                    ))
                  : title}
              </h2>
            )}

            {price ? (
              <div className={cn("text-display1 mb-4", priceClassName)}>
                {price}
              </div>
            ) : null}

            {description ? (
              <div className={cn("text-body mb-8", descriptionClassName)}>
                {description}
              </div>
            ) : null}

            {buttons.length > 0 ? (
              <div
                className={cn(
                  "xjs-icon-stack__button-container flex flex-row items-center flex-wrap mb-6",
                  Boolean(description) ? "" : "mt-4",
                  buttonContainerClassName
                )}
              >
                {buttons.length === 1
                  ? renderButton(buttons[0], 0)
                  : buttons.map((button, index) => (
                      <div
                        key={index}
                        className={cn(
                          "inline-flex",
                          index === 0 ? "mt-2 mr-6" : "mt-2 ml-0"
                        )}
                      >
                        {renderButton(button, index)}
                      </div>
                    ))}
              </div>
            ) : null}
            {legalText ? (
              <LegalDisclaimer
                disclaimers={legalText}
                variant="compact"
                className={cn("text-xs font-mono", {
                  "text-inverse-white": theme === "dark",
                  "text-black": theme !== "dark",
                })}
              />
            ) : null}
          </div>
          {customContent ? (
            <div className="relative z-10 flex items-end flex-grow">
              {customContent}
            </div>
          ) : null}
          {bottomCta && renderButton(bottomCta, 0)}
        </ZoomWrapper>
      </div>
    </>
  );
};

export default BentoCard;
